import { Injectable} from '@angular/core';
import { LessonSong } from '../../models/lesson-song'
import { ColumnWidth } from '../../models/column-width'
import { plainToClass } from 'class-transformer'
import { BehaviorSubject, Observable } from 'rxjs'
import { PreferencesProvider } from '../../providers/preferences/preferences.provider'

@Injectable({
  providedIn: 'root'
})
export class LessonSongCssService {
  readonly maxWidth: number = 1020

  widthChanged: BehaviorSubject<ColumnWidth[]>

  get detailColumnWidth(): number {
    return this.columnWidth('tonality') +
      this.columnWidth('meter') +
      this.columnWidth('swow') +
      this.columnWidth('activity') +
      this.columnWidth('rp') +
      this.columnWidth('tp') +
      this.columnWidth('instrument') +
      this.columnWidth('recording') +
      this.columnWidth('pem')
  }

  constructor(
    private preferencesProvider: PreferencesProvider
  ) {
    const columnSizes = this.preferencesProvider.getColumnSizePrefs()
    this.columnWidths.push(plainToClass(ColumnWidth, {shrinkOrder: 6, name: 'name', preferredWidth: columnSizes.find((column) => column.name === 'name').width, minWidth: 110}))
    this.columnWidths.push(plainToClass(ColumnWidth, {shrinkOrder: 1, name: 'tonality', preferredWidth: columnSizes.find((column) => column.name === 'tonality').width, minWidth: 95}))
    this.columnWidths.push(plainToClass(ColumnWidth, {shrinkOrder: 2, name: 'meter', preferredWidth: columnSizes.find((column) => column.name === 'meter').width, minWidth: 60}))
    this.columnWidths.push(plainToClass(ColumnWidth, {shrinkOrder: 3, name: 'swow', preferredWidth: columnSizes.find((column) => column.name === 'swow').width, minWidth: 60}))
    this.columnWidths.push(plainToClass(ColumnWidth, {shrinkOrder: 7, name: 'activity', preferredWidth: columnSizes.find((column) => column.name === 'activity').width, minWidth: 100}))
    this.columnWidths.push(plainToClass(ColumnWidth, {name: 'rp', preferredWidth: columnSizes.find((column) => column.name === 'rp').width, minWidth: 40}))
    this.columnWidths.push(plainToClass(ColumnWidth, {name: 'tp', preferredWidth: columnSizes.find((column) => column.name === 'tp').width, minWidth: 40}))
    this.columnWidths.push(plainToClass(ColumnWidth, {name: 'instrument', preferredWidth: columnSizes.find((column) => column.name === 'instrument').width, minWidth: 75}))
    this.columnWidths.push(plainToClass(ColumnWidth, {shrinkOrder: 4, name: 'recording', preferredWidth: columnSizes.find((column) => column.name === 'recording').width, minWidth: 100}))
    this.columnWidths.push(plainToClass(ColumnWidth, {shrinkOrder: 5, name: 'pem', preferredWidth: columnSizes.find((column) => column.name === 'pem').width, minWidth: 115}))
    this.columnWidths.push(plainToClass(ColumnWidth, {name: 'info', preferredWidth: columnSizes.find((column) => column.name === 'info').width, minWidth: 34}))

    this.calcColumnWidths(window.innerWidth)
    this.widthChanged = new BehaviorSubject<ColumnWidth[]>(null)
  }

  columnWidths: ColumnWidth[] = []
  columnWidth(columnName: string): number {
    if (columnName === 'name' && this.isResponsive) {
      return this.currWidth < 500 ? 110 : 150
    }
    const col = this.columnWidths.find(column => column.name === columnName)
    return col.width
  }

  resizeable(columnName: string): boolean {
    const col = this.columnWidths.find(column => column.name === columnName)
    return col.shrinkOrder !== undefined
  }

  minColumnWidth(columnName: string): number {
    return this.columnWidths.find(column => column.name === columnName).minWidth
  }

  setColumnWidth(columnName: string, width: number): ColumnWidth {
    const columnWidth = this.columnWidths.find( column => column.name === columnName)
    const widthToSet = Math.max(columnWidth.minWidth, width)
    columnWidth.width = widthToSet
    columnWidth.userSet = true
    let totalWidth = 0
    this.columnWidths.forEach((column: ColumnWidth) => totalWidth += column.width)
    let amountToShrink = totalWidth - this.maxWidth

    if (amountToShrink > 0) {
      this.columnWidths.sort(ColumnWidth.compare).forEach((column: ColumnWidth) => {
        if (amountToShrink > 0 && column.changeAble) {
          amountToShrink -= column.shrink(amountToShrink)
        }
      })
      if (amountToShrink > 0) {
        columnWidth.shrink(amountToShrink)
      }
    } else {
      this.columnWidths.sort(ColumnWidth.compare).forEach((column: ColumnWidth) => {
        if (amountToShrink < 0 && column.changeAble && column.name !== 'info') {
          amountToShrink -= column.expand(amountToShrink)
        }
      })
      if (amountToShrink < 0) {
        columnWidth.expand(amountToShrink)
      }
    }

    this.widthChanged.next(this.columnWidths)
    this.preferencesProvider.setColumnSizePref(this.columnWidths)
    return columnWidth
  }

  /**
   * Resets the user set field on all columns if all have been changed or we have locked
   * ourselves to the maxWidth
   */
  resetUserSet() {
    let anyChangeable = false
    let fixedWidth = 0
    this.columnWidths.forEach((column: ColumnWidth) => {
      anyChangeable = anyChangeable || column.changeAble
      fixedWidth += column.changeAble ? column.minWidth : column.width
    })

    if (!anyChangeable || fixedWidth === this.maxWidth) {
      this.columnWidths = this.columnWidths.map((column: ColumnWidth) => {
        column.userSet = false
        return column
      })
    }
  }

  currWidth: number = 0
  isResponsive: boolean = false
  calcColumnWidths(width: number) {
    if (this.currWidth === width) {
      return
    } else {
      this.currWidth = width
    }

    let totalWidth = 0
    this.columnWidths = this.columnWidths.map(column => {
      column.width = column.userSet ? column.width : column.preferredWidth
      totalWidth += column.width
      return column
    })

    if (totalWidth > this.currWidth) {
      this.isResponsive = true
    } else {
      this.isResponsive = false
    }
  }

  responsiveClass(): string {
    if (this.isResponsive) {
      return 'responsive'
    }
    return ''
  }

  isEditing: boolean = false

  swowClass(lessonSong: LessonSong): string {
    return lessonSong.isSwow ? 'mt-check' : ''
  }

  activityClass(): string {
    return this.isEditing ? 'editing' : ''
  }

  rpClass(lessonSong: LessonSong): string {
    return lessonSong.hasRp ? 'mt-check' : (this.isEditing ? 'mt-add' : '')
  }

  tpClass(lessonSong: LessonSong): string {
    return lessonSong.hasTp ? 'mt-check' : (this.isEditing ? 'mt-add' : '')
  }

  instrumentClass(lessonSong: LessonSong): string {
    return lessonSong.hasInstrument ? 'mt-check' : (this.isEditing ? 'mt-add' : '')
  }
  
  ngOnDestroy(){
    this.widthChanged.unsubscribe();
  }

  recordingClass(lessonSong: LessonSong): string {
    if (this.isEditing) {
      if (lessonSong.canPlay) {
        return 'mt-check'
      } else {
        return 'mt-add'
      }
    } else {
      if (lessonSong.canPlay) {
        if (this.playingSong === null) {
          return 'mt-play'
        }
        return this.playingSong.uuid === lessonSong.uuid ? 'mt-pause' : 'mt-play'
      } else {
        return ''
      }
    }
  }

  pemClass(lessonSong: LessonSong): string {
    if (this.isEditing) {
      return !lessonSong.pemUuid ? 'mt-plus' : 'mt-check'
    } else {
      return !lessonSong.pemUuid ? '' : 'mt-check'
    }
  }

  pemButtonClass(lessonSong: LessonSong): string {
    if (this.isEditing) {
      return !lessonSong.pemUuid ? 'add-pem' : ''
    } else {
      return !lessonSong.pemUuid ? '' : ''
    }
  }

  playingSong: LessonSong = null
}
